import { useCallback, useEffect, useState } from 'react';

import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { useDispatch, useStore, useSelector } from 'react-redux';

import { Toast } from '@whelk/components';
import { CHAT_TITLE_MAX_LENGTH, MAX_SELECT_COUNT } from '@whelk/const/src/history';
import { CHAT_BASE_URL, HOME_URL, HISTORY_URL, buildUrl } from '@whelk/const/src/routerUrl';
import { TRACK_KEYS, WINDOW_CLICK_TYPE } from '@whelk/const/src/trackSpec';
import {
  getChatHistoryListByPage,
  renameChatTitle,
  setChatFavoriteStatus,
  searchChatHistoryList,
  deleteAllHistory,
  deleteChatHistories,
} from '@whelk/services/src/chat';
import { isApiError } from '@whelk/services/src/common';
import { FAVORITE_STATUS, FavoriteStatusEnum } from '@whelk/services/src/type/chat';
import type { RootState } from '@whelk/store';
import { setChatTitle } from '@whelk/store/slices/chatListSlice';
import {
  deleteHistoryListItems,
  mergeHistoryList,
  setHistoryList,
  setSearchResultList,
  updateHistoryTitle,
  setIsLoading,
  setIsLoadingMore,
  setIsPageEnd,
  setRenameHistoryModal,
  updateHistoryFavoriteStatus,
  HISTORY_LIST_TYPE,
  HistoryListType,
  setIsHistoryDeleteAllModalOpen,
  setSelectedChatIds,
  setIsSelectMode,
  setIsOpenMobileHistoryLayer,
} from '@whelk/store/slices/historyListSlice';
import { trackCustomEvent, TrackParams } from '@whelk/utils';
import useDeleteChat from './ChatHooks/useDeleteChat';

const PAGE_SIZE = 20;

const LOAD_STRATEGY = {
  REFRESH_FIRST_PAGE: 'REFRESH_FIRST_PAGE',
  LOAD_MORE: 'LOAD_MORE',
};

type LoadStrategy = (typeof LOAD_STRATEGY)[keyof typeof LOAD_STRATEGY];

const updateHistoryList = async (
  rootState: RootState,
  dispatch: (action: any) => void,
  listType: HistoryListType,
  strategy: LoadStrategy,
  toastError: boolean,
) => {
  const { chatIds } = rootState.history[listType];
  let lastID = '0';
  if (strategy === LOAD_STRATEGY.LOAD_MORE && chatIds.length > 0) {
    lastID = chatIds[chatIds.length - 1];
    dispatch(setIsLoadingMore({ listType, isLoadingMore: true }));
  } else {
    dispatch(setIsLoading({ listType, isLoading: true }));
  }

  try {
    const data = await getChatHistoryListByPage(
      lastID,
      PAGE_SIZE,
      listType === HISTORY_LIST_TYPE.FAVORITE ? FAVORITE_STATUS.FAVORITE : undefined,
    );
    const items = data?.items || [];
    if (strategy === LOAD_STRATEGY.REFRESH_FIRST_PAGE) {
      dispatch(setHistoryList({ listType, items }));
    } else {
      dispatch(mergeHistoryList({ listType, items }));
    }
    dispatch(setIsPageEnd({ listType, isPageEnd: data?.hasMore === false }));
  } catch (err) {
    if (toastError && isApiError(err)) {
      Toast.show(err.message);
    }
  } finally {
    dispatch(setIsLoadingMore({ listType, isLoadingMore: false }));
    dispatch(setIsLoading({ listType, isLoading: false }));
  }
};

export const loadHistoriesSilently = async (dispatch: (action: any) => void) => {
  dispatch(setIsLoading({ listType: HISTORY_LIST_TYPE.ALL, isLoading: true }));
  dispatch(setIsLoading({ listType: HISTORY_LIST_TYPE.FAVORITE, isLoading: true }));
  try {
    const [historyData, favoriteData] = await Promise.all([
      getChatHistoryListByPage('0', PAGE_SIZE, undefined),
      getChatHistoryListByPage('0', PAGE_SIZE, FAVORITE_STATUS.FAVORITE),
    ]);
    dispatch(setHistoryList({ listType: HISTORY_LIST_TYPE.ALL, items: historyData?.items ?? [] }));
    dispatch(
      setHistoryList({ listType: HISTORY_LIST_TYPE.FAVORITE, items: favoriteData?.items ?? [] }),
    );
    dispatch(
      setIsPageEnd({ listType: HISTORY_LIST_TYPE.ALL, isPageEnd: historyData?.hasMore === false }),
    );
    dispatch(
      setIsPageEnd({
        listType: HISTORY_LIST_TYPE.FAVORITE,
        isPageEnd: favoriteData?.hasMore === false,
      }),
    );
  } finally {
    dispatch(setIsLoading({ listType: HISTORY_LIST_TYPE.ALL, isLoading: false }));
    dispatch(setIsLoading({ listType: HISTORY_LIST_TYPE.FAVORITE, isLoading: false }));
  }
};

export const useLoadHistories = () => {
  const dispatch = useDispatch();

  const loadSilently = useCallback(() => {
    loadHistoriesSilently(dispatch);
  }, []);

  return { loadSilently };
};

export const useHistoryList = (listType: HistoryListType) => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const isLogin = useSelector((state: RootState) => state.user.isLogin);

  const updateList = async (strategy: LoadStrategy, toastError: boolean, onFinish?: () => void) => {
    await updateHistoryList(store.getState(), dispatch, listType, strategy, toastError);
    onFinish?.();
  };

  const refresh = async (toastError: boolean, callback?: () => void) => {
    if (!isLogin) return;
    updateList(LOAD_STRATEGY.REFRESH_FIRST_PAGE, toastError, callback);
  };

  const loadMore = async (toastError: boolean) => {
    if (!isLogin) return;
    updateList(LOAD_STRATEGY.LOAD_MORE, toastError);
  };

  const clear = () => {
    dispatch(setHistoryList({ listType, items: [] }));
  };

  return {
    refresh, // 刷新第一页
    loadMore, // 加载更多页
    clear, // 清空列表
  };
};

export const useDeleteHistory = () => {
  const dispatch = useDispatch();
  const nav = useRouter();
  const { t } = useTranslation();
  const { onDeleteHistory } = useDeleteChat();

  const showDeleteChatModal = (chatIds: string[], trackParams?: TrackParams) => {
    // dispatch(setDeletingHistoryModal({ open: true, chatIds, trackParams }));
    onDeleteHistory({ chatIds, trackParams });
    trackCustomEvent({
      id: 'chat_delete_popup_wnd_view',
      [TRACK_KEYS.CHAT_ID_LIST]: chatIds.join(','),
      [TRACK_KEYS.CHAT_NUM]: chatIds.length,
      ...trackParams,
    });
  };

  // const cancelDeleteChat = (chatIds?: string[], trackParams?: TrackParams) => {
  //   // dispatch(setDeletingHistoryModal({ open: false }));
  //   chatIds &&
  //     trackCustomEvent({
  //       id: 'chat_delete_popup_wnd_click',
  //       [TRACK_KEYS.CHAT_ID_LIST]: chatIds.join(','),
  //       [TRACK_KEYS.CHAT_NUM]: chatIds.length,
  //       [TRACK_KEYS.CLICK_TYPE]: WINDOW_CLICK_TYPE.CANCEL,
  //       ...trackParams,
  //     });
  // };

  // 删除弹窗点 确定
  const confirmDeleteChat = async (
    chatIds: string[],
    currentChatId: string,
    trackParams?: TrackParams,
  ) => {
    trackCustomEvent({
      id: 'chat_delete_popup_wnd_click',
      [TRACK_KEYS.CHAT_ID_LIST]: chatIds.join(','),
      [TRACK_KEYS.CHAT_NUM]: chatIds.length,
      [TRACK_KEYS.CLICK_TYPE]: WINDOW_CLICK_TYPE.CONFIRM,
      ...trackParams,
    });
    try {
      await deleteChatHistories(chatIds);
      Toast.success(t('toast_delete_msg_success'));
      dispatch(deleteHistoryListItems(chatIds));
      dispatch(setSelectedChatIds({ chatIds: [], isSelectingAll: false }));
      if (chatIds[0] === currentChatId) {
        nav.replace(HOME_URL);
      }
    } catch (err) {
      if (isApiError(err)) {
        Toast.error(err.message);
      }
    }
  };

  const showDeleteAllHistoryModal = async () => {
    dispatch(setIsHistoryDeleteAllModalOpen(true));
  };

  const cancelDeleteAllHistory = () => {
    dispatch(setIsHistoryDeleteAllModalOpen(false));
    trackCustomEvent({ id: 'delete_all_popup_wnd_click', clk_type: 2 });
  };

  const confirmDeleteAllHistory = async () => {
    dispatch(setIsHistoryDeleteAllModalOpen(false));
    try {
      await deleteAllHistory();
      trackCustomEvent({
        id: 'delete_all_popup_wnd_click',
        page: 'chat_history_page',
        clk_type: 1,
      });
      Toast.success(t('toast_delete_msg_success'));
      dispatch(setHistoryList({ listType: HISTORY_LIST_TYPE.ALL, items: [] }));
      dispatch(setHistoryList({ listType: HISTORY_LIST_TYPE.FAVORITE, items: [] }));
    } catch (err) {
      if (isApiError(err)) {
        Toast.error(err.message);
      }
    }
  };

  return {
    showDeleteChatModal,
    confirmDeleteChat,
    showDeleteAllHistoryModal,
    cancelDeleteAllHistory,
    confirmDeleteAllHistory,
  };
};

export const useRenameHistory = () => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { t } = useTranslation();

  const showRenameChatModal = async (chatId: string, trackParams?: TrackParams) => {
    dispatch(setRenameHistoryModal({ open: true, chatId, trackParams }));
    trackCustomEvent({
      id: 'chat_rename_popup_wnd_view',
      [TRACK_KEYS.CHAT_ID]: chatId,
      ...trackParams,
    });
  };

  const cancelRenameChat = (chatId?: string, trackParams?: TrackParams) => {
    dispatch(setRenameHistoryModal({ open: false }));
    chatId &&
      trackCustomEvent({
        id: 'chat_rename_popup_wnd_click',
        [TRACK_KEYS.CHAT_ID]: chatId,
        [TRACK_KEYS.CLICK_TYPE]: WINDOW_CLICK_TYPE.CANCEL,
        ...trackParams,
      });
  };

  // 删除弹窗点 确定
  const confirmRenameChat = async (chatId: string, newTitle: string, trackParams?: TrackParams) => {
    if (newTitle.length > CHAT_TITLE_MAX_LENGTH) {
      Toast.error(t('history_rename_max_length_toast', { count: CHAT_TITLE_MAX_LENGTH }));
      return;
    }
    const historyItem = store.getState().history.historyItemById[chatId];
    if (!historyItem) return;
    const originTitle = historyItem.chatTitle;
    const currentChatId = store.getState().chat.chatId;
    dispatch(setRenameHistoryModal({ open: false }));
    dispatch(updateHistoryTitle({ chatId, newTitle }));
    trackCustomEvent({
      id: 'chat_rename_popup_wnd_click',
      [TRACK_KEYS.CHAT_ID]: chatId,
      [TRACK_KEYS.CLICK_TYPE]: WINDOW_CLICK_TYPE.CONFIRM,
      ...trackParams,
    });
    try {
      await renameChatTitle(chatId, newTitle);
      Toast.success(t('history_rename_success_toast'));
      if (chatId === currentChatId) {
        dispatch(setChatTitle(newTitle));
      }
    } catch (err) {
      if (isApiError(err)) {
        Toast.error(err.message);
      }
      // 如果失败了，就改回去
      dispatch(updateHistoryTitle({ chatId, newTitle: originTitle }));
    }
  };

  return {
    showRenameChatModal,
    cancelRenameChat,
    confirmRenameChat,
  };
};

export const useFavoriteHistory = () => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { t } = useTranslation();
  const refreshFavoriteList = () => {
    updateHistoryList(
      store.getState(),
      dispatch,
      HISTORY_LIST_TYPE.FAVORITE,
      LOAD_STRATEGY.REFRESH_FIRST_PAGE,
      false,
    );
  };

  const setHistoryFavoriteStatus = async (
    chatIds: string[],
    favoriteStatus: FavoriteStatusEnum,
  ) => {
    try {
      await setChatFavoriteStatus(chatIds, favoriteStatus);
      dispatch(updateHistoryFavoriteStatus({ chatIds, favoriteStatus }));
      dispatch(setSelectedChatIds({ chatIds: [], isSelectingAll: false }));
      refreshFavoriteList();
      if (favoriteStatus === FAVORITE_STATUS.FAVORITE) {
        Toast.success(t('toast_star_done', { count: chatIds.length }));
      } else {
        Toast.success(t('toast_item_unfavorited', { count: chatIds.length }));
      }
    } catch (err) {
      if (isApiError(err)) {
        Toast.error(err.message);
      }
    }
  };

  return {
    setHistoryFavoriteStatus,
  };
};

export const useHistorySearch = () => {
  const listType = HISTORY_LIST_TYPE.SEARCH;
  const dispatch = useDispatch();
  const [searchQuery, search] = useState<string>('');

  const searchFetch = useCallback(async (keyword: string) => {
    dispatch(setIsLoading({ listType, isLoading: true }));
    try {
      const data = await searchChatHistoryList(keyword);
      dispatch(setSearchResultList({ items: data?.items ?? [] }));
      dispatch(setIsPageEnd({ listType, isPageEnd: true }));
    } catch (err) {
      if (isApiError(err)) {
        Toast.show(err.message);
      }
    } finally {
      dispatch(setIsLoading({ listType, isLoading: false }));
    }
  }, []);

  const clear = useCallback(() => {
    dispatch(setHistoryList({ listType, items: [] }));
  }, []);

  useEffect(() => {
    if (searchQuery) {
      searchFetch(searchQuery);
    } else {
      clear();
    }
  }, [searchQuery]);

  return {
    searchQuery,
    search,
  };
};

export const useBatchManageHistory = () => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { t } = useTranslation();

  const switchBatchMode = (on: boolean, listType?: HistoryListType) => {
    dispatch(
      setIsSelectMode({
        isSelectMode: on,
        listType,
      }),
    );
  };

  const selectChats = (chatIds: string[]) => {
    const selectedChatIds = store.getState().history.selectedChatIds;
    if (selectedChatIds.length + chatIds.length > MAX_SELECT_COUNT) {
      Toast.warning(t('history_select_max_count_toast', { count: MAX_SELECT_COUNT }));
      return;
    }
    dispatch(
      setSelectedChatIds({ chatIds: [...selectedChatIds, ...chatIds], isSelectingAll: false }),
    );
  };

  const unselectChats = (chatIds: string[]) => {
    const selectedChatIds = store.getState().history.selectedChatIds;
    dispatch(
      setSelectedChatIds({
        chatIds: selectedChatIds.filter((id) => !chatIds.includes(id)),
        isSelectingAll: false,
      }),
    );
  };

  const selectAllChats = () => {
    const selectingListType = store.getState().history.selectingListType;
    const allChatIds = selectingListType
      ? store.getState().history[selectingListType].chatIds.slice(0, MAX_SELECT_COUNT)
      : undefined;

    if (!allChatIds) return;
    dispatch(setSelectedChatIds({ chatIds: allChatIds, isSelectingAll: true }));
  };

  const clearSelectedChats = () => {
    dispatch(setSelectedChatIds({ chatIds: [], isSelectingAll: false }));
  };

  return {
    switchBatchMode,
    selectChats,
    unselectChats,
    selectAllChats,
    clearSelectedChats,
  };
};

export const useHistoryItem = (chatId: string, searchResult = false) => {
  const store = useStore<RootState>();
  const dispatch = useDispatch();
  const nav = useRouter();
  const historyItem = useSelector((state: RootState) => {
    return searchResult
      ? state.history.searchResultById[chatId]
      : state.history.historyItemById[chatId];
  });

  const navToChat = (chatId: string) => {
    nav.push(`${CHAT_BASE_URL}=${chatId}`);
  };

  const goChatHistory = useCallback(
    (chatId: string, trackParams?: TrackParams) => {
      const isMobileHistoryLayerOpen = store.getState().history.isOpenMobileHistoryLayer;
      if (isMobileHistoryLayerOpen) {
        dispatch(setIsOpenMobileHistoryLayer(false));
        // 等 modal 动画结束再跳转
        setTimeout(() => {
          navToChat(chatId);
        }, 300);
      } else {
        navToChat(chatId);
      }
      trackCustomEvent({
        id: 'chat_history_click',
        [TRACK_KEYS.CHAT_ID]: chatId,
        ...trackParams,
      });
    },
    [store],
  );

  return {
    historyItem,
    goChatHistory,
  };
};

export const useHistoryListRoute = () => {
  const nav = useRouter();
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const searchParams = useSearchParams();

  const navToHistoryList = (listType: HistoryListType = HISTORY_LIST_TYPE.ALL) => {
    const params = {
      type: listType,
    };
    const url = buildUrl(HISTORY_URL, params);
    nav.push(url);
  };

  const goHistoryList = (listType: HistoryListType = HISTORY_LIST_TYPE.ALL) => {
    navToHistoryList(listType);
    const isMobileHistoryLayerOpen = store.getState().history.isOpenMobileHistoryLayer;
    if (isMobileHistoryLayerOpen) {
      setTimeout(() => {
        dispatch(setIsOpenMobileHistoryLayer(false));
      }, 300);
    }
  };

  const getHistoryTypeFromUrl = () => {
    return searchParams?.get('type') as HistoryListType;
  };

  return {
    goHistoryList,
    getHistoryTypeFromUrl,
  };
};
